import { vec2 } from 'gl-matrix';
import { V } from '../misc/Vector.js';
import { Intersection } from './Intersection.js';
import { VectorState } from './VectorState.js';
import Flatten from '@flatten-js/core';
export { GeometryUtils };
class GeometryUtils {
    static segmentWithWidth(pointA, pointB, width) {
        const vector = V.subtract(pointB, pointA);
        const left = V.toLength(V.leftOrthogonal(vector), width);
        const right = V.toLength(V.rightOrthogonal(vector), width);
        return [
            V.add(pointA, left),
            V.add(pointA, right),
            V.add(pointB, left),
            V.add(pointB, right)
        ];
    }
    static arcLength(pointA, pointB, center, radius, rotation) {
        const vA = V.subtract(pointA, center);
        const vB = V.subtract(pointB, center);
        const arc = new Flatten.Arc(new Flatten.Point(center[0], center[1]), radius, GeometryUtils.normalizedAngle(vA), GeometryUtils.normalizedAngle(vB), rotation > 0);
        return arc.length;
    }
    static normalizedAngle(vector) {
        const angle = Math.atan2(vector[1], vector[0]);
        return angle < 0 ? angle + 2 * Math.PI : angle;
    }
    static adjacentCircleOfSegment(point, segmentPoint1, segmentPoint2) {
        const EPSILON = 1e-3;
        const ON_EPSILON = 1e-2;
        const MIN_LENGTH = 1;
        const [px, py] = point, [s1x, s1y] = segmentPoint1, [s2x, s2y] = segmentPoint2;
        const sxd = s2x - s1x, syd = s2y - s1y, sds = sxd * sxd + syd * syd;
        if (sds < MIN_LENGTH) {
            return undefined;
        }
        else {
            const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds, ct = t;
            if (ct < ON_EPSILON || 1 - ct < ON_EPSILON) {
                return undefined;
            }
            const dx = ct * sxd, dy = ct * syd, cpx = s1x + dx, cpy = s1y + dy, dpx = px - cpx, dpy = py - cpy, len = Math.hypot(dx, dy);
            if (Math.abs(dpx * sxd + dpy * syd) < EPSILON) {
                return [Math.hypot(dpx, dpy), len];
            }
            else {
                return undefined;
            }
        }
    }
    static distancePointSegment(point, segmentPoint1, segmentPoint2) {
        const [px, py] = point, [s1x, s1y] = segmentPoint1, [s2x, s2y] = segmentPoint2;
        const sxd = s2x - s1x, syd = s2y - s1y, sds = sxd * sxd + syd * syd;
        let distance;
        if (sds === 0) {
            const pxd = px - s1x, pyd = py - s1y;
            distance = pxd * pxd + pyd * pyd;
        }
        else {
            const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds, ct = Math.max(0, Math.min(t, 1)), cpx = s1x + ct * sxd, cpy = s1y + ct * syd, fxd = px - cpx, fyd = py - cpy;
            distance = fxd * fxd + fyd * fyd;
        }
        return Math.sqrt(distance);
    }
    static closestPointOnSegmentWithRelativePosition(point, segmentPoint1, segmentPoint2) {
        const MIN_LENGTH = 1e-3;
        const [px, py] = point, [s1x, s1y] = segmentPoint1, [s2x, s2y] = segmentPoint2;
        const sxd = s2x - s1x, syd = s2y - s1y, sds = sxd * sxd + syd * syd;
        if (sds < MIN_LENGTH) {
            return undefined;
        }
        else {
            const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds, cpx = s1x + t * sxd, cpy = s1y + t * syd;
            return [t, [cpx, cpy]];
        }
    }
    static closestPointOnSegment(point, segmentPoint1, segmentPoint2) {
        const [px, py] = point, [s1x, s1y] = segmentPoint1, [s2x, s2y] = segmentPoint2;
        const sxd = s2x - s1x, syd = s2y - s1y, sds = sxd * sxd + syd * syd;
        if (sds === 0) {
            return [s1x, s1y];
        }
        else {
            const t = ((px - s1x) * sxd + (py - s1y) * syd) / sds, ct = Math.max(0, Math.min(t, 1)), cpx = s1x + ct * sxd, cpy = s1y + ct * syd;
            return [cpx, cpy];
        }
    }
    static negate(v) {
        return [v[0] !== 0 ? -v[0] : 0, v[1] !== 0 ? -v[1] : 0];
    }
    static vectorTo(a, b) {
        return GeometryUtils.toVector(vec2.subtract([0, 0], b, a));
    }
    static toVector(v) {
        return [v[0], v[1]];
    }
    static leftOrthogonal(v) {
        return [v[1] !== 0 ? -v[1] : 0, v[0]];
    }
    static rightOrthogonal(v) {
        return [v[1], v[0] !== 0 ? -v[0] : 0];
    }
    static toLength(v, length) {
        return GeometryUtils.toVector(vec2.scale([0, 0], v, length / vec2.length(v)));
    }
    static rotation(a, b) {
        return vec2.cross([0, 0, 0], a, b)[2];
    }
    static vectorState(a, b) {
        return {
            cross: GeometryUtils.rotation(a, b),
            dot: vec2.dot(a, b)
        };
    }
    static isOutsideInclusiveVector(right, left, probe) {
        const span = VectorState.of(right, left), sright = VectorState.of(right, probe), sleft = VectorState.of(left, probe), leftAngle = span.isLeft() || span.isCollinear(), inside = leftAngle ? sright.isLeft() && sleft.isRight() : sright.isRight() && sleft.isLeft();
        return leftAngle ? !inside : (inside || sright.isSameDirection() || sleft.isSameDirection());
    }
    static isInsideInclusiveVector(right, left, probe) {
        const span = VectorState.of(right, left), sright = VectorState.of(right, probe), sleft = VectorState.of(left, probe), leftAngle = span.isLeft() || span.isCollinear(), inside = leftAngle ? sright.isLeft() && sleft.isRight() : sright.isRight() && sleft.isLeft();
        return leftAngle ? (inside || sright.isSameDirection() || sleft.isSameDirection()) : !inside;
    }
    static isInsideInclusiveVector2(right, left, probe) {
        const rightAngle = Math.atan2(right[1], right[0]), leftAngle = Math.atan2(left[1], left[0]), probeAngle = Math.atan2(probe[1], probe[0]), isRegular = rightAngle <= leftAngle;
        return isRegular ? (probeAngle >= rightAngle && probeAngle <= leftAngle) : (probeAngle <= leftAngle || probeAngle >= rightAngle);
    }
    static isInsideInclusive(right, left, probe) {
        return right <= left ? (probe >= right && probe <= left) : (probe <= left || probe >= right);
    }
    static areOutsideInclusiveSameBend(rightBase, leftBase, probe) {
        if (probe[0] === probe[1]) {
            return rightBase === leftBase && rightBase === probe[0];
        }
        else {
            let left, right;
            if (GeometryUtils.isInsideInclusive(probe[0], rightBase, probe[1])) {
                [right, left] = [probe[1], probe[0]];
            }
            else if (GeometryUtils.isInsideInclusive(probe[1], rightBase, probe[0])) {
                [right, left] = [probe[0], probe[1]];
            }
            else {
                /* istanbul ignore next */
                throw new Error('Impossible case.');
            }
            if (!GeometryUtils.isInsideInclusive(leftBase, right, left)) {
                return false;
            }
            const leftInvert = GeometryUtils.invertAngle(left);
            return !GeometryUtils.isRight(leftInvert, right);
        }
    }
    static invertAngle(angle) {
        return angle > 0 ? angle - Math.PI : (angle - Math.PI) + 2 * Math.PI;
    }
    static isRight(base, probe) {
        return base > 0 ? probe < base && probe > GeometryUtils.invertAngle(base) : probe < base || probe > GeometryUtils.invertAngle(base);
    }
    static isRightOrSameDirection(a, b) {
        const s = VectorState.of(a, b);
        return s.isRight() || s.isSameDirection();
    }
    static isLeftOrSameDirection(a, b) {
        const s = VectorState.of(a, b);
        return s.isLeft() || s.isSameDirection();
    }
    static isLeftOrCollinear(a, b) {
        const s = VectorState.of(a, b);
        return s.isLeft() || s.isCollinear();
    }
    static isSameDirection(a, b) {
        return VectorState.of(a, b).isSameDirection();
    }
    static isOrthogonal(a, b) {
        return VectorState.of(a, b).isOrthogonal();
    }
    static polygonIsClockwise(points) {
        if (points.length < 3) {
            throw new Error('Unexpected input');
        }
        return points.reduce((accumulator, currentValue) => {
            accumulator[1] += (currentValue[0] - accumulator[0][0]) * (currentValue[1] + accumulator[0][1]);
            accumulator[0] = currentValue;
            return accumulator;
        }, [points[points.length - 1], 0])[1] > 0;
    }
    static boundingBox(points) {
        const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
        return {
            minX: Math.min(...xs),
            maxX: Math.max(...xs),
            minY: Math.min(...ys),
            maxY: Math.max(...ys)
        };
    }
    static circlesInnerTangents(centerA, radiusA, centerB, radiusB) {
        const EPSILON = 1e-12;
        const radiusTotal = radiusA + radiusB, dir = V.subtract(centerB, centerA), d = V.norm(dir);
        /* If circles touch in one point, there is only one tangent */
        if (Math.abs(d - radiusTotal) < EPSILON) {
            return V.add(centerA, V.toLength(dir, radiusA));
        }
        /* If centers closer to each other than added radii, no tangents exist */
        if (d < radiusTotal) {
            return [];
        }
        const centerMid = V.multiply(V.add(centerA, centerB), 0.5), radiusMid = 0.5 * d, intersection = Intersection.circleCircle(centerB, radiusTotal, centerMid, radiusMid);
        /* istanbul ignore if */
        if (!Array.isArray(intersection) || intersection.length === 0 || intersection.length === 1) {
            /*
             * This actually cannot happen, due to the choice of circles
             * The case of length === 1 was handled previously (only one tangent)
             */
            throw new Error('Impossible case');
        }
        /*
         * The first resulting tangent is the line (centerA - intersection point 1),
         * shifted by radiusA, perpendicularly towards centerB;
         * For the second tangent analogously.
         */
        const shift1 = V.toLength(V.subtract(centerB, intersection[0]), radiusA), shift2 = V.toLength(V.subtract(centerB, intersection[1]), radiusA);
        return [
            [
                V.add(centerA, shift1),
                V.add(intersection[0], shift1)
            ],
            [
                V.add(centerA, shift2),
                V.add(intersection[1], shift2)
            ]
        ];
    }
    static circlesOuterTangents(centerA, radiusA, centerB, radiusB) {
        const EPSILON = 1e-12;
        let centerLarger = centerA, radiusLarger = radiusA, centerSmaller = centerB, radiusSmaller = radiusB, largerA = true;
        if (radiusA < radiusB) {
            centerLarger = centerB;
            radiusLarger = radiusB;
            centerSmaller = centerA;
            radiusSmaller = radiusA;
            largerA = false;
        }
        const radiusDiff = radiusLarger - radiusSmaller, dir = V.subtract(centerSmaller, centerLarger), d = V.norm(dir);
        if (radiusDiff > d) {
            return [];
        }
        else if (Math.abs(radiusDiff - d) < EPSILON) {
            return V.add(centerSmaller, V.toLength(dir, radiusSmaller));
        }
        else if (Math.abs(radiusDiff) < EPSILON) {
            const shift1 = V.toLength(V.leftOrthogonal(dir), radiusLarger), shift2 = V.toLength(V.rightOrthogonal(dir), radiusLarger);
            return largerA ? [
                [
                    V.add(centerLarger, shift1),
                    V.add(centerSmaller, shift1)
                ],
                [
                    V.add(centerLarger, shift2),
                    V.add(centerSmaller, shift2)
                ]
            ] : [
                [
                    V.add(centerSmaller, shift1),
                    V.add(centerLarger, shift1)
                ],
                [
                    V.add(centerSmaller, shift2),
                    V.add(centerLarger, shift2)
                ]
            ];
        }
        const radiusMid = 0.5 * d, centerMid = V.add(centerLarger, V.toLength(dir, radiusMid)), intersection = Intersection.circleCircle(centerLarger, radiusDiff, centerMid, radiusMid);
        if (!Array.isArray(intersection) || intersection.length === 0) {
            /*
             * This actually cannot happen, due to the choice of circles
             */
            return [];
            throw new Error('Impossible case');
        }
        let v1, v2;
        let i1, i2;
        if (intersection.length === 1) {
            v1 = V.leftOrthogonal(dir);
            v2 = V.rightOrthogonal(dir);
            i1 = centerLarger;
            i2 = centerLarger;
        }
        else {
            /* istanbul ignore if */
            if (intersection.length !== 2) {
                throw new Error('Impossible case');
            }
            v1 = V.subtract(intersection[0], centerLarger);
            v2 = V.subtract(intersection[1], centerLarger);
            i1 = intersection[0];
            i2 = intersection[1];
        }
        const shift1 = V.toLength(v1, radiusSmaller), shift2 = V.toLength(v2, radiusSmaller);
        return largerA ? [
            [
                V.add(i1, shift1),
                V.add(centerSmaller, shift1)
            ],
            [
                V.add(i2, shift2),
                V.add(centerSmaller, shift2)
            ]
        ] : [
            [
                V.add(centerSmaller, shift1),
                V.add(i1, shift1)
            ],
            [
                V.add(centerSmaller, shift2),
                V.add(i2, shift2)
            ]
        ];
    }
    static pointCircleTangents(point, center, radius) {
        const EPSILON = 1e-12;
        const dir = V.subtract(center, point), d = V.norm(dir);
        if (Math.abs(d - radius) < EPSILON) {
            return point;
        }
        if (d < radius) {
            return [];
        }
        const radiusMid = 0.5 * d, centerMid = V.add(point, V.toLength(dir, radiusMid)), intersection = Intersection.circleCircle(center, radius, centerMid, radiusMid);
        /* istanbul ignore if */
        if (!Array.isArray(intersection) || intersection.length === 0 || intersection.length === 1) {
            /*
             * This actually cannot happen, due to the choice of circles
             * The case of length === 1 was handled previously (point is on circle)
             */
            throw new Error('Impossible case');
        }
        return [
            [
                point,
                intersection[0]
            ],
            [
                point,
                intersection[1]
            ]
        ];
    }
}
//# sourceMappingURL=Geometry.js.map